/**
 * mortar — 榴彈炮。動詞:**預判潑群**。
 *
 * 玩法:間隔較慢、單發強。每發砲彈走**拋物線**飛到前方一個落點,**落地範圍爆炸**對圈內
 * 全敵人施 AoE 傷害。落點固定在砲口前方一段距離(玩家用走位讓落點壓到敵群)。一發潑一群,
 * 但因為間隔慢 + 落點固定,要預判敵人會走到哪。
 *
 * 設計:砲彈是「武器自己擁有的小實體」(起點/落點/飛行進度),behavior 每幀:
 *   1. 到間隔 → 生一發(算好落點 landingX/landingY,飛行 flightSec)。
 *   2. 每發推進飛行進度 t(0→1),scene 用 mortarArcOffsetY 畫拋物軌跡。
 *   3. t 到 1(落地)→ 對落點半徑內全活敵 fireMortar(AoE 施傷 + 爆炸 FX),砲彈消失。
 * 純落點/AoE 邏輯用 types.ts 純函式(mortarLandingX/mortarArcOffsetY/aoeHitIndices)。
 * 鐵則:威力 = baseBulletDamage × 倍率 × 等級曲線,不讀修煉值。
 */

import type { WeaponBehavior, WeaponTickContext } from './types';
import {
  mortarLandingX,
  mortarArcOffsetY,
  aoeHitIndices,
  weaponLevelDamageMult,
  applyRateMod,
  sizeRangeMult,
  blastRangeBonus,
  extraHitChains,
  pierceDamageFalloff,
  LANE_DAMAGE_FALLOFF,
} from './types';

export const MORTAR_ID = 'mortar';
export const MORTAR_BLURB = '慢慢拋出榴彈,落地炸一圈 — 預判敵人會聚在哪,一發潑一群。';

/** 發射間隔(ms):刻意慢(單發強)。 */
export const MORTAR_FIRE_INTERVAL_MS = 1300;
/** 砲彈水平初速(px/sec):決定落點離砲口多遠(配合 flightSec)。 */
export const MORTAR_MUZZLE_SPEED = 320;
/** 飛行時間(sec):從發射到落地。拋物上拱也用這段時間做。 */
export const MORTAR_FLIGHT_SEC = 0.9;
/** 拋物最高點(px,相對直線往上):純視覺,讓拋物明顯。 */
export const MORTAR_ARC_PEAK = 120;
/** 爆炸 AoE 半徑(px):落點這個圈內全敵人吃同傷(潑群)。 */
export const MORTAR_BLAST_RADIUS = 84;
/**
 * 傷害倍率:慢射單發重(一發要打得有感)。
 * boss 2026-06-14 第二輪「算範圍×傷害」:mortar 是 range★4(落地 AoE 潑一群 = 廣)。
 * 3.2→2.9 微降,讓單體 DPS 收進帶內 — range★4 的價值是「一發炸一圈多隻」,不該對單一
 * 王也頂標單發傷害(避免又廣又高把王秒)。AoE 多目標的好處不受此調影響。
 */
export const MORTAR_DAMAGE_MULT = 2.9;
/** 落點最遠到場景右緣留白(避免飛出畫面外炸不到)。scene 可覆寫,headless 用此預設。 */
export const MORTAR_MAX_LANDING_X = 760;
/** 一次多發榴彈時,額外砲彈落點相對主落點的前後錯位(px,做成「散落一片」)。 */
export const MORTAR_VOLLEY_LANDING_STAGGER = 70;
/** 子爆(穿透/彈跳詮釋)落點相對主落點的步進偏移(px,往前一段一個子爆)。 */
export const MORTAR_SUBBLAST_STEP = 64;

/** 一發在飛的砲彈(scene adapter 讀來畫拋物軌跡 + 砲彈點)。 */
export interface MortarShell {
  originX: number;
  originY: number;
  landingX: number;
  landingY: number;
  /** 飛行進度 0..1(0=剛發射, 1=落地)。 */
  t: number;
  /** 爆炸半徑(px)。 */
  radius: number;
  /** 落地 AoE 傷害。 */
  damage: number;
  /**
   * 子爆次數(三選一「穿透/彈跳」詮釋)。落地後在主落點前方各 step 再炸這麼多次
   * (連環爆穿過敵群)。0 = 只炸主落點(預設,行為同今日)。
   */
  subBlasts: number;
}

export class MortarBehavior implements WeaponBehavior {
  readonly id = MORTAR_ID;
  private _shells: MortarShell[] = [];
  private _lastFireMs = -1;
  /** 場景右緣落點上限(scene 在 reset 後可透過 setMaxLandingX 覆寫;預設常數)。 */
  private _maxLandingX = MORTAR_MAX_LANDING_X;

  tick(ctx: WeaponTickContext): void {
    // 三選一詮釋 — mortar 版:
    //   攻擊增速 → 拋射間隔縮短(interval × effectiveAutoFireMult,單發更密)。
    //   多顆子彈 → 一次拋多發榴彈(多落點,前後錯位 = 散落一片)。
    //   巨彈/爆裂 → AoE 半徑加大(radius ×bulletSizeMult + spreadBlastRadius)。
    //   穿透/彈跳 → 落地後連環子爆(往前 step 各炸一次,穿過敵群)。
    const mods = ctx.mods;

    // 1. 到間隔生一「齊射」(主彈 + effectiveSpreadBullets 發額外榴彈,落點前後錯位)。
    const intervalMs = applyRateMod(MORTAR_FIRE_INTERVAL_MS, mods.effectiveAutoFireMult());
    if (this._lastFireMs < 0 || ctx.nowMs - this._lastFireMs >= intervalMs) {
      this._lastFireMs = ctx.nowMs;
      const originX = ctx.playerX + 30;
      const baseLandingX = mortarLandingX(originX, MORTAR_MUZZLE_SPEED, MORTAR_FLIGHT_SEC, this._maxLandingX);
      const dmg = Math.round(
        ctx.baseBulletDamage() * MORTAR_DAMAGE_MULT * weaponLevelDamageMult(ctx.weaponLevel),
      );
      // 巨彈/爆裂 → 爆炸半徑加大(× 巨彈倍率 + 爆裂半徑)。
      const radius = MORTAR_BLAST_RADIUS * sizeRangeMult(mods.bulletSizeMult) + blastRangeBonus(mods.spreadBlastRadius);
      // 穿透/彈跳 → 落地後連環子爆次數。
      const subBlasts = extraHitChains(mods.pierceCount, mods.bounceCount + mods.evoBounceBonus);
      const extraShells = mods.effectiveSpreadBullets();
      const total = 1 + extraShells;
      for (let i = 0; i < total; i++) {
        // 多發落點前後錯位(主彈居中,額外彈交錯往前/後一段)。clamp 不超過右緣上限。
        const stagger = i === 0 ? 0 : (i % 2 === 1 ? -1 : 1) * Math.ceil(i / 2) * MORTAR_VOLLEY_LANDING_STAGGER;
        const landingX = Math.min(Math.max(originX, baseLandingX + stagger), this._maxLandingX);
        this._shells.push({
          originX,
          originY: ctx.playerY,
          landingX,
          landingY: ctx.playerY, // 落在玩家當前那條 y(瞄準=走位)
          t: 0,
          radius,
          damage: dmg,
          subBlasts,
        });
      }
    }

    // 2~3. 推進每發砲彈飛行;落地 → AoE 爆炸(+ 連環子爆)、砲彈消失。
    const survivors: MortarShell[] = [];
    for (const s of this._shells) {
      s.t += ctx.dt / MORTAR_FLIGHT_SEC;
      if (s.t < 1) {
        survivors.push(s);
        continue;
      }
      const targets = ctx.enemies();
      // 主落點 + subBlasts 個往前 step 的連環子爆。各落點都炸一圈。
      // 廣命中傷害衰減(boss 2026-06-14「不能打得又廣傷害又高」):主落點滿傷,第 N 個
      // 往前的連環子爆(穿過敵群的「廣」)吃 damage × LANE_DAMAGE_FALLOFF^N → 廣不滿傷。
      for (let b = 0; b <= s.subBlasts; b++) {
        const cx = Math.min(s.landingX + b * MORTAR_SUBBLAST_STEP, this._maxLandingX);
        const blastDmg = pierceDamageFalloff(s.damage, b, LANE_DAMAGE_FALLOFF);
        if (ctx.fireMortar) {
          ctx.fireMortar(cx, s.landingY, s.radius, blastDmg);
        } else {
          // 無 mortar seam 的退化路徑:直接對命中目標施傷(仍是 AoE 效果,只是無 FX)。
          const hits = aoeHitIndices(targets, cx, s.landingY, s.radius);
          for (const i of hits) {
            const t = targets[i];
            if (t && t.alive) t.takeDamage(blastDmg);
          }
        }
      }
      // 落地即消失(不 push 進 survivors)。
    }
    this._shells = survivors;
  }

  reset(): void {
    this._shells = [];
    this._lastFireMs = -1;
  }

  /** scene adapter 用:設定落點右緣上限(依實際畫面寬)。 */
  setMaxLandingX(x: number): void {
    if (Number.isFinite(x) && x > 0) this._maxLandingX = x;
  }

  /** scene adapter / 測試讀來畫拋物軌跡或斷言。回唯讀快照。 */
  shells(): readonly MortarShell[] {
    return this._shells;
  }

  /** scene FX 用:把砲彈飛行進度 t 換成「相對直線的上拱 y 位移」。 */
  arcOffsetY(t: number): number {
    return mortarArcOffsetY(t, MORTAR_ARC_PEAK);
  }
}
